// Implements: F-UIX-019, F-GEN-036, F-UIX-007
/**
 * The catalogue picker of one Modelos tier, searching the server (mockup v3 P7, contract C7.2).
 *
 * The search is sent a moment after the admin stops typing (250 ms, through an injectable
 * `schedule` so tests never wait on the clock, checklist 16); the «Solo con razonamiento» switch
 * searches at once. Each search is its own query, so a late answer to an older search is never
 * shown for the newer one (checklist 14); while a search is read the previous list stays on
 * screen. A `503` (the provider did not answer) says so, and the model can still be typed by hand
 * in the tier's own field. A pick says what it did to the prices (a toast: «Precios rellenados
 * desde el catálogo», or that the catalogue gives none). It is mounted only while the picker is open.
 */

import { useEffect, useState } from "react";
import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { useToast } from "../../../shared/ui/molecules/ToastProvider";
import type { CatalogueModelView } from "../../../types/modelCatalogue";
import { ModelCataloguePicker } from "../components/ModelCataloguePicker";
import type { CatalogueState } from "../components/ModelCataloguePicker";
import { useModelCatalogue } from "../hooks/useModelCatalogue";

/** Runs `run` after `ms` and returns the way to cancel it. */
export type Schedule = (run: () => void, ms: number) => () => void;

export const SEARCH_DEBOUNCE_MS = 250;

const timeoutSchedule: Schedule = (run, ms) => {
  const handle = setTimeout(run, ms);
  return () => {
    clearTimeout(handle);
  };
};

export interface ModelCataloguePickerContainerProps {
  /** The model the tier uses now. */
  readonly selectedModelId: string;
  readonly onPick: (model: CatalogueModelView) => void;
  readonly schedule?: Schedule | undefined;
}

export function ModelCataloguePickerContainer({
  selectedModelId,
  onPick,
  schedule = timeoutSchedule,
}: ModelCataloguePickerContainerProps): ReactElement {
  const { t } = useTranslation();
  const { push } = useToast();
  const [typed, setTyped] = useState("");
  const [searched, setSearched] = useState("");
  const [reasoningOnly, setReasoningOnly] = useState(false);

  useEffect(() => {
    if (typed === searched) return undefined;
    return schedule(() => {
      setSearched(typed);
    }, SEARCH_DEBOUNCE_MS);
  }, [typed, searched, schedule]);

  const catalogue = useModelCatalogue({ q: searched, reasoningOnly });

  function picked(model: CatalogueModelView): void {
    // Say what the pick did to the prices: filled from the catalogue, or left for the admin to type.
    push({ tone: "info", message: model.price === null ? t("models.picker.noPrice") : t("models.picker.filled") });
    onPick(model);
  }

  let state: CatalogueState = { kind: "loading" };
  if (catalogue.isError) state = { kind: "unavailable" };
  else if (catalogue.data !== undefined) state = { kind: "ready", view: catalogue.data };

  return (
    <ModelCataloguePicker
      query={typed}
      onQuery={setTyped}
      reasoningOnly={reasoningOnly}
      onReasoningOnly={setReasoningOnly}
      state={state}
      selectedModelId={selectedModelId}
      onPick={picked}
    />
  );
}
